import _ from 'underscore';
import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { ActionButton, Stack } from '@fluentui/react';

import { styles as defaultStyles } from './styles';
import { isUrlCustomParametersEmpty } from './validators';
import { UrlOptionsParameters } from './url-options-parameters';

export function UrlOptions(props) {
  const {
    i18n,
    styles,
    onChange,
    trackingUrlTemplate,
    finalUrlSuffix,
    urlCustomParameters,
    errors,
    enableSelfValidation,
    showUurlTestTool,
    showFinalUrlSuffix,
    token,
    triggerTestUurl,
    instrumentation,
  } = props;

  const [editMode, setEditMode] = useState(false);
  const onToggleButtonClick = useCallback(() => {
    setEditMode(_.negate(Boolean));
  }, []);

  return (
    <Stack
      className="url-options"
      styles={styles}
    >
      <ActionButton
        toggle
        className="url-options-toggle-button"
        checked={editMode}
        text={i18n.getString(_TL_('Ad URL options (optional)'))}
        iconProps={{ iconName: editMode ? 'ChevronDown' : 'ChevronRight' }}
        onClick={onToggleButtonClick}
        aria-expanded={editMode}
      />
      <div className="url-options-descriptions">
        {i18n.getString(_TL_('Set up the tracking and redirect information for your ads.'))}
      </div>
      {editMode ?
        <UrlOptionsParameters
          i18n={i18n}
          instrumentation={instrumentation}
          errors={errors}
          enableSelfValidation={enableSelfValidation}
          trackingUrlTemplate={trackingUrlTemplate}
          finalUrlSuffix={finalUrlSuffix}
          urlCustomParameters={urlCustomParameters}
          onChange={onChange}
          showFinalUrlSuffix={showFinalUrlSuffix}
          showUurlTestTool={showUurlTestTool}
          token={token}
          triggerTestUurl={triggerTestUurl}
        />
        :
        <Stack className="url-options-parameters--read-only">
          <div className="url-options-parameters__param_label--read-only">
            {i18n.getString(_TL_('Tracking template:'))}
          </div>
          <div className="url-options-parameters__param_value--read-only tracking-template">
            <span>{_.isEmpty(trackingUrlTemplate) ? i18n.getString(_TL_('None')) : trackingUrlTemplate}</span>
          </div>
          {showFinalUrlSuffix &&
            <React.Fragment>
              <div className="url-options-parameters__param_label--read-only">
                {i18n.getString(_TL_('Final URL suffix:'))}
              </div>
              <div className="url-options-parameters__param_value--read-only final-url-suffix">
                <span>{_.isEmpty(finalUrlSuffix) ? i18n.getString(_TL_('None')) : finalUrlSuffix}</span>
              </div>
            </React.Fragment>
          }
          <div className="url-options-parameters__param_label--read-only">
            {i18n.getString(_TL_('Custom parameters:'))}
          </div>
          <div className="url-options-parameters__param_value--read-only">
            {isUrlCustomParametersEmpty(urlCustomParameters) ? i18n.getString(_TL_('None')) :
              _.map(urlCustomParameters, ({ Key, Value }) => (
                _.isEmpty(Key) ? null : <div>{`{_${Key}} = ${Value}`}</div>
              ))
            }
          </div>
        </Stack>
      }
    </Stack>
  );
}

UrlOptions.propTypes = {
  styles: PropTypes.shape({}),

  trackingUrlTemplate: PropTypes.string,

  finalUrlSuffix: PropTypes.string,

  urlCustomParameters: PropTypes.arrayOf(PropTypes.shape({
    Key: PropTypes.string,
    Value: PropTypes.string,
  })),

  errors: PropTypes.arrayOf(PropTypes.shape({
    Code: PropTypes.string,
    Message: PropTypes.string,
    Property: PropTypes.string,
    Index: PropTypes.number,
  })),

  enableSelfValidation: PropTypes.bool,

  onChange: PropTypes.func,

  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,

  showUurlTestTool: PropTypes.bool,

  showFinalUrlSuffix: PropTypes.bool,

  token: PropTypes.string,

  triggerTestUurl: PropTypes.func,

  instrumentation: PropTypes.shape({}),
};

UrlOptions.defaultProps = {
  styles: defaultStyles,
  trackingUrlTemplate: '',
  finalUrlSuffix: '',
  urlCustomParameters: [],
  errors: [],
  enableSelfValidation: false,
  onChange: _.noop,
  showUurlTestTool: false,
  showFinalUrlSuffix: false,
  token: '',
  triggerTestUurl: _.noop,
  instrumentation: {},
};
